import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { API, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Printer, ArrowLeft, Loader2, Lock, FileText, Image as ImageIcon, CreditCard, LayoutGrid, Download, Check, ScanLine } from "lucide-react";

const FORMATS = [
  { key: "a4", label: "Cartaz A4", icon: FileText, hint: "Colar no vidro" },
  { key: "postal", label: "Cartão Postal", icon: ImageIcon, hint: "Com foto" },
  { key: "card", label: "Cartão de Visita", icon: CreditCard, hint: "Entregar na mão" },
  { key: "card_sheet", label: "10 por folha", icon: LayoutGrid, hint: "Cartões de visita" },
];
const COLORS = [
  { key: "red", label: "Vermelho", swatch: "#FF3B30" },
  { key: "black", label: "Preto", swatch: "#0A0A0A" },
  { key: "white", label: "Branco", swatch: "#FFFFFF", border: true },
];
const HEADLINES = ["VENDE-SE", "ABAIXO DA FIPE", "ACEITO TROCA"];

export default function Cartaz() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [format, setFormat] = useState("a4");
  const [color, setColor] = useState("red");
  const [photoIndex, setPhotoIndex] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [downloading, setDownloading] = useState(false);
  const [headlineMode, setHeadlineMode] = useState("VENDE-SE");
  const [customHeadline, setCustomHeadline] = useState("");
  const [qrTested, setQrTested] = useState(false);

  useEffect(() => {
    api.get("/listings/mine").then(({ data }) => {
      const c = data.find((x) => x.id === id) || false;
      setCar(c);
      if (c && c.qr_verified) setQrTested(true);
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-blaze" /></div>;
  if (!car || !car.poster_paid) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6">
        <Lock className="w-10 h-10 text-blaze" />
        <h1 className="mt-4 font-heading font-800 text-2xl">Cartaz não disponível</h1>
        <p className="mt-2 text-muted-foreground">Você precisa gerar (pagar) o cartaz deste anúncio primeiro.</p>
        <Button className="mt-5 bg-obsidian hover:bg-blaze text-white rounded-sm" onClick={() => navigate("/painel")}>Voltar ao painel</Button>
      </div>
    );
  }

  const images = car.images || [];
  const showPhoto = format !== "a4" && images.length > 0;
  const isSheet = format === "card_sheet";
  const headline = (headlineMode === "custom" ? customHeadline : headlineMode).trim().toUpperCase() || "VENDE-SE";
  const params = `format=${format}&color=${color}&photo_index=${photoIndex}&per_page=${perPage}&headline=${encodeURIComponent(headline)}`;
  const cardUrl = `${API}/listings/${id}/card.png?${params}`;
  const isPortrait = format === "a4" || format === "card_sheet";
  const scanUrl = `${window.location.origin}/anuncio/${id}`;

  const download = async (ext) => {
    if (!qrTested) { toast.error("Confirme que testou o QR Code antes de baixar."); return; }
    setDownloading(true);
    try {
      const res = await api.get(`/listings/${id}/card.${ext}?${params}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `piccar-${format}.${ext}`; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Arquivo ${ext.toUpperCase()} baixado!`);
    } catch { toast.error("Não foi possível gerar o arquivo."); }
    finally { setDownloading(false); }
  };

  const handlePrint = () => {
    if (!qrTested) { toast.error("Confirme que testou o QR Code antes de imprimir."); return; }
    window.print();
  };

  const toggleQrTested = async (checked) => {
    setQrTested(checked);
    if (checked && !car.qr_verified) {
      try {
        await api.post(`/listings/${id}/qr-verified`);
        setCar((c) => ({ ...c, qr_verified: true }));
        toast.success("QR confirmado! Um selo aparecerá no seu anúncio.");
      } catch { /* silencioso: confirmação local ainda vale */ }
    }
  };

  return (
    <div className="min-h-screen bg-secondary py-8 px-4">
      <div className="no-print max-w-3xl mx-auto mb-6">
        <div className="flex items-center justify-between mb-5">
          <button onClick={() => navigate("/painel")} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-obsidian" data-testid="cartaz-back">
            <ArrowLeft className="w-4 h-4" /> Painel
          </button>
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-sm font-700" onClick={() => download("png")} disabled={downloading || !qrTested} data-testid="download-png-btn">
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-2" />} PNG
            </Button>
            <Button variant="outline" className="rounded-sm font-700" onClick={() => download("pdf")} disabled={downloading || !qrTested} data-testid="download-pdf-btn">
              <Download className="w-4 h-4 mr-2" /> PDF
            </Button>
            <Button className="bg-blaze hover:bg-obsidian text-white font-700 rounded-sm" onClick={handlePrint} disabled={!qrTested} data-testid="print-btn">
              <Printer className="w-4 h-4 mr-2" /> Imprimir
            </Button>
          </div>
        </div>

        {/* formatos */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 bg-white border border-border rounded-sm p-2" data-testid="format-selector">
          {FORMATS.map((f) => (
            <button key={f.key} onClick={() => setFormat(f.key)} data-testid={`format-${f.key}`}
              className={`flex flex-col items-center gap-1 py-3 px-2 rounded-sm border transition-colors text-center ${format === f.key ? "border-blaze bg-blaze/5" : "border-transparent hover:bg-secondary"}`}>
              <f.icon className={`w-5 h-5 ${format === f.key ? "text-blaze" : "text-muted-foreground"}`} strokeWidth={1.6} />
              <span className={`text-sm font-700 ${format === f.key ? "text-obsidian" : "text-foreground"}`}>{f.label}</span>
              <span className="text-[11px] text-muted-foreground hidden sm:block">{f.hint}</span>
            </button>
          ))}
        </div>

        {/* texto de destaque */}
        <div className="mt-3 bg-white border border-border rounded-sm p-3" data-testid="headline-selector">
          <span className="text-xs font-700 uppercase tracking-wide text-muted-foreground">Texto de destaque</span>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {HEADLINES.map((h) => (
              <button key={h} onClick={() => setHeadlineMode(h)} data-testid={`headline-${h}`}
                className={`px-3 py-1.5 text-sm font-700 rounded-sm border transition-colors ${headlineMode === h ? "border-blaze bg-blaze/5 text-obsidian" : "border-border text-muted-foreground hover:border-obsidian"}`}>
                {h}
              </button>
            ))}
            <button onClick={() => setHeadlineMode("custom")} data-testid="headline-custom-btn"
              className={`px-3 py-1.5 text-sm font-700 rounded-sm border transition-colors ${headlineMode === "custom" ? "border-blaze bg-blaze/5 text-obsidian" : "border-border text-muted-foreground hover:border-obsidian"}`}>
              Outro texto
            </button>
            {headlineMode === "custom" && (
              <Input value={customHeadline} onChange={(e) => setCustomHeadline(e.target.value)} maxLength={22}
                placeholder="Ex.: ÚNICO DONO" className="h-9 w-44 rounded-sm text-sm uppercase" data-testid="headline-custom-input" />
            )}
          </div>
        </div>

        {/* cor + foto */}
        <div className="mt-3 flex flex-wrap items-center gap-4 bg-white border border-border rounded-sm p-3">
          <div className="flex items-center gap-2">
            <span className="text-xs font-700 uppercase tracking-wide text-muted-foreground mr-1">Cor</span>
            {COLORS.map((c) => (
              <button key={c.key} onClick={() => setColor(c.key)} data-testid={`color-${c.key}`} title={c.label}
                className={`w-8 h-8 rounded-full transition-transform ${color === c.key ? "ring-2 ring-offset-2 ring-obsidian scale-110" : ""} ${c.border ? "border border-border" : ""}`}
                style={{ background: c.swatch }} />
            ))}
          </div>
          {showPhoto && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-700 uppercase tracking-wide text-muted-foreground mr-1">Foto</span>
              {images.map((img, i) => (
                <button key={i} onClick={() => setPhotoIndex(i)} data-testid={`photo-${i}`}
                  className={`w-12 h-10 rounded-sm overflow-hidden border-2 transition-colors ${photoIndex === i ? "border-blaze" : "border-transparent"}`}>
                  <img src={fileUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
          {isSheet && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-700 uppercase tracking-wide text-muted-foreground mr-1">Por folha</span>
              {[4, 6, 10].map((n) => (
                <button key={n} onClick={() => setPerPage(n)} data-testid={`perpage-${n}`}
                  className={`px-3 py-1.5 text-sm font-700 rounded-sm border transition-colors ${perPage === n ? "border-blaze bg-blaze/5 text-obsidian" : "border-border text-muted-foreground hover:border-obsidian"}`}>
                  {n}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* confirmação de leitura do QR */}
        <div className={`mt-3 rounded-sm border p-3 transition-colors ${qrTested ? "border-emerald-500 bg-emerald-50" : "border-amber-400 bg-amber-50"}`} data-testid="qr-check-box">
          <div className="flex items-start gap-3">
            <ScanLine className={`w-5 h-5 mt-0.5 shrink-0 ${qrTested ? "text-emerald-600" : "text-amber-600"}`} strokeWidth={1.8} />
            <div className="flex-1">
              <p className="text-sm font-700 text-obsidian">Antes de imprimir, teste o QR Code</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Aponte a câmera do celular para o QR na prévia ao lado e confirme que ele abre o seu anúncio.{" "}
                <a href={scanUrl} target="_blank" rel="noreferrer" className="text-blaze font-700 underline" data-testid="qr-preview-link">Ver meu anúncio</a>
              </p>
              <label className="mt-2 flex items-center gap-2 cursor-pointer select-none" data-testid="qr-tested-label">
                <input type="checkbox" checked={qrTested} onChange={(e) => toggleQrTested(e.target.checked)} className="sr-only peer" data-testid="qr-tested-checkbox" />
                <span className={`w-5 h-5 rounded-sm border flex items-center justify-center transition-colors ${qrTested ? "bg-emerald-600 border-emerald-600" : "border-obsidian bg-white"}`}>
                  {qrTested && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </span>
                <span className={`text-sm font-700 ${qrTested ? "text-emerald-700" : "text-obsidian"}`}>Testei com a câmera e o QR abriu meu anúncio</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* preview = arquivo final gerado no backend */}
      <div className="print-sheet flex justify-center">
        <div className={`print-card bg-white shadow-2xl ${isPortrait ? "max-w-[420px]" : "max-w-[720px]"} w-full`} data-testid="poster">
          <img src={cardUrl} alt="Cartaz PicCar" className="w-full h-auto block" data-testid="card-image" />
        </div>
      </div>

      <p className="no-print text-center text-xs text-muted-foreground mt-6 max-w-md mx-auto">
        {format === "a4" && "Cartaz A4 para colar no vidro."}
        {format === "postal" && "Cartão postal (14,8 × 10,5 cm) com foto do carro."}
        {format === "card" && "Cartão de visita (8,5 × 5,4 cm) para entregar na mão."}
        {format === "card_sheet" && `Folha A4 com ${perPage} cartões de visita — imprima e recorte pelas linhas.`}
        {" "}Baixe em PNG/PDF ou imprima. Só este QR Code abre seu anúncio.
      </p>
    </div>
  );
}
